import React, { useEffect, useState } from 'react';
import CascaDeModal from '../shared/CascaDeModal';
import { procurementService } from '../../services/api';
import { fmtDate, fmtDateTime } from '../../utils/formatters';
import { TONE_BADGE } from '../../utils/statusColors';
import { autorDaVersao } from '../../utils/novaVersaoDoDocumento';

/**
 * HISTÓRICO DE VERSÕES de um documento de Licitações (GET /procurement/documents/:id/versions, backend #540).
 *
 * ⛔ Duas listas, e a separação é a regra do servidor: `versoes` é a cadeia REGISTRADA (previousVersionId); os inativos
 * do mesmo tipo sem elo (renovações anteriores a 27/10/2026) vêm em `possiveisAnteriores`, com o motivo, e aparecem À
 * PARTE, nunca misturados na cadeia — juntar afirmaria uma linhagem que ninguém registrou.
 */
type Linha = {
  id: number; fileName?: string | null; issueDate?: string | null; expiryDate?: string | null; isActive?: boolean;
  noExpiry?: boolean; archived?: boolean; temArquivo?: boolean; createdAt?: string | null; criadoPor?: string | null;
};

export default function HistoricoDeVersoesModal({ doc, onClose }: { doc: any; onClose: () => void }) {
  const [dados, setDados] = useState<{ versoes: Linha[]; possiveisAnteriores: { docs: Linha[]; motivo: string } } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    procurementService.versoesDoDocumento(doc.id)
      .then((r: any) => { if (vivo) setDados({ versoes: r?.versoes || [], possiveisAnteriores: r?.possiveisAnteriores || { docs: [], motivo: '' } }); })
      .catch((e: any) => { if (vivo) setErro(e?.response?.data?.message || e?.message || 'erro desconhecido'); });
    return () => { vivo = false; };
  }, [doc.id]);

  const abrir = async (l: Linha) => {
    // A aba abre no clique (antes do await), senão o bloqueador de pop-up a recusa.
    const aba = window.open('', '_blank');
    setAbrindo(l.id);
    try {
      const { url } = await procurementService.getDocumentUrl(l.id);
      if (aba) { aba.opener = null; aba.location.href = url; } else window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      aba?.close();
      setErro(`Não consegui abrir o arquivo da versão #${l.id}.`);
    }
    setAbrindo(null);
  };

  const linha = (l: Linha, semVinculo = false) => (
    <li key={l.id} data-versao={l.id} className="flex items-start justify-between gap-3 py-2.5 border-b border-central-borda last:border-0">
      <div className="min-w-0 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-central-texto break-all">{l.fileName || 'sem arquivo'}</span>
          {l.isActive && !semVinculo && <span className={`px-1.5 py-0.5 rounded text-[11px] ${TONE_BADGE.success}`}>atual</span>}
          {l.archived && <span className={`px-1.5 py-0.5 rounded text-[11px] ${TONE_BADGE.neutral}`}>arquivado</span>}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          Emissão {fmtDate(l.issueDate)} · {l.noExpiry ? 'sem validade' : `validade ${fmtDate(l.expiryDate)}`}
        </div>
        <div className="text-xs text-slate-500 mt-0.5" data-autor>
          Subida em {fmtDateTime(l.createdAt)} · por {autorDaVersao(l.criadoPor)}
        </div>
      </div>
      {l.temArquivo && (
        <button type="button" onClick={() => void abrir(l)} disabled={abrindo === l.id}
          className="shrink-0 px-3 py-1.5 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto text-xs disabled:opacity-50">
          {abrindo === l.id ? 'Abrindo…' : 'Abrir'}
        </button>
      )}
    </li>
  );

  return (
    <CascaDeModal titulo={`Histórico de versões · ${doc.name}`} onClose={onClose} largura="md">
      <div className="space-y-4" data-historico-de-versoes={doc.id}>
        {erro && <div role="alert" className={`px-3 py-2 rounded-lg text-xs ${TONE_BADGE.danger}`}>Não consegui carregar o histórico: {erro}</div>}
        {!dados && !erro && <div className="text-sm text-slate-400">Carregando as versões…</div>}
        {dados && (
          <>
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Versões registradas</h3>
              <ol>{dados.versoes.map((l) => linha(l))}</ol>
              {dados.versoes.length <= 1 && (
                <p className="text-xs text-slate-400 mt-1">Ainda não há versão anterior registrada deste documento.</p>
              )}
            </section>
            {dados.possiveisAnteriores.docs.length > 0 && (
              <section data-possiveis-anteriores>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Possíveis versões anteriores, sem vínculo</h3>
                <p className="text-xs text-slate-400 mb-1">Documentos inativos do mesmo tipo; {dados.possiveisAnteriores.motivo}. Não dá para afirmar que são versões deste.</p>
                {/* O servidor manda na ordem da tabela; aqui, do mais novo para o mais antigo, como a cadeia. */}
                <ul>{[...dados.possiveisAnteriores.docs].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''))).map((l) => linha(l, true))}</ul>
              </section>
            )}
          </>
        )}
      </div>
    </CascaDeModal>
  );
}
